<template>
  <div class="w-100 mt-3" style="position: relative">
    <div class="mb-2">
      <div
        v-if="!isCurrentLocationLoading"
        class="pm-live-search type-2 w-100 mb-2 map-input-group"
        style="height: 40px"
      >
        <multiple-input-group :is-popup-manageable-by-parent="true" :is-popup-visible-parent="isResultsVisible">
          <template #front-item>
            <nc-input
              v-model="keyword"
              placeholder="Search a location"
              @on-keyup-enter="onSearch()"
              @on-keyup="onKeyupLocation()"
            />
          </template>
          <template #group>
            <div v-if="isResultsLoading"><loader /></div>
            <div v-else-if="addressSearchResults && addressSearchResults.length > 0">
              <div
                v-for="(resultItem, index) in addressSearchResults"
                :key="`resultItem-${index}`"
                class="address-search-result-item tw-mb-2 tw-flex tw-flex-col tw-px-3 tw-py-3"
                @click="onClickedResultItem(resultItem)"
              >
                <lazy-library-text :text="resultItem.name"></lazy-library-text>
                <lazy-library-text
                  type="small"
                  style="color: #adadad"
                  :text="resultItem.description"
                ></lazy-library-text>
              </div>
            </div>
            <div v-else-if="!addressSearchResults || addressSearchResults.length === 0">Couldn't find the address</div>
          </template>
        </multiple-input-group>
      </div>
      <div v-if="!isCurrentLocationLoading">
        <neuron-google-map
          v-model:map-center-position="location"
          :is-draggable-marker="true"
          :marker-color="markerColor"
          @on-position-changed="onPositionChanged()"
        />
      </div>
      <div v-else class="d-flex flex-row justify-content-center">
        <loader />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { clickingOutsideDirective } from '~/units/core/directives/clickingOutside.directive';
import NeuronGoogleMap from '~/components/common/NeuronGoogleMap.vue';
import NcInput from '~/components/common/inputs/NcInput.vue';
import MultipleInputGroup from '~/components/common/MultipleInputGroup.vue';
import Loader from '~/components/common/Loader.vue';
import { SubComponentMixin } from '~/mixins/subComponent.mixin';
import type { ContactAddress } from '~/units/contacts/types';
import { debounce } from '~/units/core/providers/debounce.provider';
import { useAddressesStore } from '~/units/addresses/store';

export default defineNuxtComponent({
  name: 'ManuelAddress',

  mixins: [SubComponentMixin],

  props: {
    fullAddress: {
      required: true,
      type: [Object, null] as PropType<ContactAddress | null>,
    },
  },

  components: {
    Loader,
    MultipleInputGroup,
    NcInput,
    NeuronGoogleMap,
  },

  directives: {
    clickingOutsideDirective,
  },

  data() {
    return {
      keyword: '',
      isResultsVisible: false,
      isResultsLoading: false,
      addressSearchResults: [],

      location: {
        lat: 51.5265526,
        lng: -0.1160449,
      },

      isCurrentLocationLoading: false,

      handler: null,
    };
  },

  computed: {
    markerColor() {
      return this.colors['brown--secondary'];
    },

    fullAddressModelValue: {
      get(): ContactAddress | null {
        return this.fullAddress;
      },
      set(value: ContactAddress | null) {
        this.$emit('update:fullAddress', value);
      },
    },
  },

  mounted() {
    this.createHandler();
    this.getPermissionToAccessLocation();
  },

  methods: {
    getPermissionToAccessLocation() {
      if (process.client) {
        try {
          if (navigator.geolocation) {
            this.isCurrentLocationLoading = true;
            navigator.geolocation.getCurrentPosition(this.handleBrowserLocation, this.handleBrowserLocationErrors);
          }
        } catch (e) {}
      }
    },

    async handleBrowserLocation(e: { coords: { latitude: number; longitude: number } }) {
      if (e.coords && e.coords.latitude && e.coords.longitude) {
        try {
          this.locationChanged(e.coords.latitude, e.coords.longitude);

          await this.onPositionChanged();
        } catch (e) {}
      }
      this.isCurrentLocationLoading = false;
    },

    handleBrowserLocationErrors() {
      this.isCurrentLocationLoading = false;
    },

    async onPositionChanged() {
      const addressesStore = useAddressesStore();
      const { data } = await addressesStore.getGeocodeByLatLong(this.location.lat, this.location.lng);
      if (data) {
        this.onCenterChanged(data);
      }
    },

    async onSearch() {
      this.isResultsVisible = true;
      this.isResultsLoading = true;

      const addressesStore = useAddressesStore();
      const result = await addressesStore.searchPlaces(this.keyword, true);

      this.isResultsLoading = false;
      this.addressSearchResults = result.data;
    },

    createHandler() {
      if (!this.handler) {
        this.handler = debounce(this.onSearch, 500);
      }
    },

    onKeyupLocation() {
      if (this.handler) {
        this.handler();
      }
    },

    locationChanged(lat: number, lng: number) {
      this.location = {
        lat,
        lng,
      };
    },

    async onClickedResultItem(item) {
      const addressesStore = useAddressesStore();

      const { data } = await addressesStore.getGeocodeByPlaceId(item.place_id);

      this.locationChanged(data.geometry.location.lat, data.geometry.location.lng);
      this.prepareAddress(data);
      this.isResultsVisible = false;
    },

    onCenterChanged(e: { formatted_address: string }) {
      this.prepareAddress(e);
    },

    prepareAddress(e) {
      const addressComponents = e.address_components.map((item) => {
        return {
          ...item,
          type: item.types && item.types.length > 0 ? item.types[0] : null,
        };
      });

      const address1 = (() => {
        const address = addressComponents.find((x) => x.type === 'street_number');
        return address ? address.long_name : null;
      })();

      const address2 = (() => {
        const address = addressComponents.find((x) => x.type === 'route');
        return address ? address.long_name : null;
      })();

      const address3 = (() => {
        const neighborhoodAddress = addressComponents.find((x) => x.type === 'neighborhood');
        const subLocalityAddress = addressComponents.find((x) => x.type === 'sublocality');

        if (neighborhoodAddress && subLocalityAddress) {
          return `${neighborhoodAddress.long_name} ${subLocalityAddress.long_name}`;
        }
        if (neighborhoodAddress) {
          return neighborhoodAddress.long_name;
        }

        if (subLocalityAddress) {
          return subLocalityAddress.long_name;
        }

        return null;
      })();

      const address4 = (() => {
        const address = addressComponents.find((x) => x.type === 'administrative_area_level_2');
        return address ? address.long_name : null;
      })();

      const address5 = (() => {
        const postalTownAddress = addressComponents.find((x) => x.type === 'postal_town');
        const localityAddress = addressComponents.find((x) => x.type === 'locality');

        if (postalTownAddress && localityAddress) {
          return `${postalTownAddress.long_name} ${localityAddress.long_name}`;
        }
        if (postalTownAddress) {
          return postalTownAddress.long_name;
        }

        if (localityAddress) {
          return localityAddress.long_name;
        }

        return null;
      })();

      const postcode = (() => {
        const address = addressComponents.find((x) => x.type === 'postal_code');
        return address ? address.long_name : null;
      })();

      const countryCode = (() => {
        const address = addressComponents.find((x) => x.type === 'country');
        return address ? address.short_name : null;
      })();

      this.onAddressChanged({
        address_1: address1,
        address_2: address2,
        address_3: address3,
        address_4: address4,
        address_5: address5,
        postcode,
        country_code: countryCode,
        display_name: e.formatted_address,
        long: e.geometry.location.lng,
        lat: e.geometry.location.lat,
        country_id: null,
      });
    },

    onAddressChanged(e) {
      this.fullAddressModelValue = {
        ...e,
      };
    },
  },
});
</script>

<style scoped>
.link-text {
}

.link-text:hover {
  text-decoration: underline;
}

.loading-gif {
  height: 30px;
  width: 30px;
}

.map-input-group {
  position: absolute;
  width: 90% !important;
  top: 10px;
  right: 18px;
  border-radius: 6px;
  z-index: 999999999999;
}

.address-search-result-item {
}

.address-search-result-item:hover {
  background-color: #f8f8f8;
}
</style>
